import { obterConfiguracao } from '@/config/runtime';
import { ErroDaGeoEngine, interpretarErro, } from '@/services/geo-engine/errors';
const CABECALHO_DE_REQUEST_ID = 'X-Request-Id';
function gerarRequestId() {
    // `randomUUID` exige contexto seguro; o fallback mantém a correlação em HTTP simples.
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `viewer-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}
/** Base da API como URL absoluta: o MapLibre busca tiles em workers e não resolve caminhos relativos. */
export function baseAbsolutaDaApi() {
    const { apiBaseUrl } = obterConfiguracao();
    return new URL(apiBaseUrl, window.location.href).href.replace(/\/+$/, '');
}
/**
 * Cabeçalhos de credencial para um recurso da Geo-Engine buscado fora do `fetch` do cliente,
 * como os tiles do mapa. Devolve `null` para qualquer URL que não pertença à API configurada.
 */
export function cabecalhosDeCredencial(url) {
    const { apiToken } = obterConfiguracao();
    if (apiToken === null || !url.startsWith(`${baseAbsolutaDaApi()}/`)) {
        return null;
    }
    return { Authorization: `Bearer ${apiToken}` };
}
function montarUrl(caminho, parametros) {
    const { apiBaseUrl } = obterConfiguracao();
    const consulta = new URLSearchParams();
    for (const [chave, valor] of Object.entries(parametros ?? {})) {
        if (typeof valor === 'string' || typeof valor === 'number') {
            consulta.set(chave, String(valor));
        }
        else if (valor !== undefined) {
            for (const item of valor) {
                consulta.append(chave, item);
            }
        }
    }
    const sufixo = consulta.toString();
    return `${apiBaseUrl}${caminho}${sufixo === '' ? '' : `?${sufixo}`}`;
}
async function lerCorpo(resposta) {
    const tipo = resposta.headers.get('Content-Type') ?? '';
    if (!tipo.includes('application/json')) {
        return null;
    }
    try {
        return (await resposta.json());
    }
    catch {
        return null;
    }
}
/**
 * Executa uma chamada à Geo-Engine e devolve os dados junto do registro da chamada.
 * Nenhuma regra de negócio vive aqui: apenas transporte, correlação e medição.
 */
export async function chamar(opcoes) {
    const { metodo, caminho, parametros, corpo, autenticada = true, credencialAdministrativa, statusAdicionaisAceitos, sinal, } = opcoes;
    const url = montarUrl(caminho, parametros);
    const requestId = gerarRequestId();
    const cabecalhos = {
        Accept: 'application/json',
        [CABECALHO_DE_REQUEST_ID]: requestId,
    };
    if (credencialAdministrativa !== undefined) {
        cabecalhos.Authorization = `Bearer ${credencialAdministrativa}`;
    }
    else {
        const { apiToken } = obterConfiguracao();
        if (autenticada && apiToken !== null) {
            cabecalhos.Authorization = `Bearer ${apiToken}`;
        }
    }
    const corpoEhFormData = corpo instanceof FormData;
    if (corpo !== undefined && !corpoEhFormData) {
        // `FormData` define seu próprio `Content-Type` com boundary; o navegador cuida disso.
        cabecalhos['Content-Type'] = 'application/json';
    }
    const inicio = performance.now();
    let resposta;
    try {
        resposta = await fetch(url, {
            method: metodo,
            headers: cabecalhos,
            body: corpo === undefined ? undefined : corpoEhFormData ? corpo : JSON.stringify(corpo),
            signal: sinal,
        });
    }
    catch (falha) {
        const registro = {
            metodo,
            endpoint: caminho,
            status: 0,
            requestId,
            duracaoMs: Math.round(performance.now() - inicio),
            horario: new Date().toISOString(),
        };
        if (falha instanceof DOMException && falha.name === 'AbortError') {
            throw falha;
        }
        throw new ErroDaGeoEngine('falha_de_rede', 'Não foi possível alcançar a Geo-Engine. Verifique o endereço configurado e o CORS.', registro);
    }
    const registro = {
        metodo,
        endpoint: caminho,
        status: resposta.status,
        // O identificador devolvido pela Geo-Engine prevalece sobre o gerado aqui.
        requestId: resposta.headers.get(CABECALHO_DE_REQUEST_ID) ?? requestId,
        duracaoMs: Math.round(performance.now() - inicio),
        horario: new Date().toISOString(),
    };
    const conteudo = await lerCorpo(resposta);
    const aceita = resposta.ok || (statusAdicionaisAceitos ?? []).includes(resposta.status);
    if (!aceita) {
        throw interpretarErro(conteudo, registro);
    }
    return { dados: conteudo, registro };
}
